<template>
  <div class="modal-content">
    <div class="modal-header">
      <h4 class="modal-title">{{ _('Payment link') }}</h4>
    </div>
    <div class="modal-body">
      <div class="row">
        <div class="col-md-12">
          <div class="form-group">
  					<input class="form-control" type="text" :value="paymentLink"> 
          </div>
        </div>
      </div>
      <div class="row" v-if="versions && versions.length">
        <div class="col-xs-12">
          <div class="form-group">
            <select class="form-control" v-model="paymentLinkVersion">
              <option v-for="version in versions" :value="version">{{ getVersionName(version) }}</option>
            </select>
          </div>
        </div>
      </div>
      <div class="row">
        <div class="col-xs-12">
          <p>{{ _('You can send this link to your customer so that they can pay the order with a payment method of their choosing.') }}</p>
        </div>
      </div>
      <div class="row">
        <div class="col-xs-4">
          <div class="form-group">
            <label for="expirationDate">{{ _('Order and payment link valid until') }}</label>
            <DatePicker v-model="expirationDate" :shortLocale="shortLocale" :staticInput="true"></DatePicker>
          </div>
        </div>
        <div class="col-xs-8">
          <div class="form-group">
            <label>&nbsp;</label>
            <div class="input-group">
              <div class="input-group-button">
                <button type="button" class="btn btn-success" id="setExpirationDate" v-on:click="setExpirationDate"><i id="setExpirationDateIcon" :class="'fa fa-fw fa-' + setExpirationButtonIcon"></i> {{ _('Set') }}</button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="row">
        <div class="col-xs-12">
          <p>{{ _('You can optionally set a due date for the payment link. If the order has not been paid by this date, both the order and the payment link will be automatically deleted.') }}
          {{ _('If you don\'t set a date, the order will remain in the system indefinitely, and you will have to manually handle unpaid orders.') }}</p>
        </div>
      </div>
      <div class="row">
        <div class="col-md-12">
          <div class="form-group">
            <label for="email">{{ _('Email') }}</label>
  					<input id="email" class="form-control" type="email" v-model="paymentLinkEmail"> 
          </div>
        </div>
      </div>
      <div class="row">
        <div class="col-md-12">
          <div class="form-group">
            <label for="subject">{{ _('Message subject') }}</label>
  					<input id="subject" class="form-control" type="subject" v-model="paymentLinkSubject"> 
          </div>
        </div>
      </div>
      <div class="row">
        <div class="col-md-12">
          <div class="form-group">
            <label for="message">{{ _('Message') }}</label>
            <textarea class="form-control" id="message" rows="5" wrap="virtual" v-model="paymentLinkMessage"></textarea>
          </div>
        </div>
      </div>
    </div>
    <div class="modal-footer">
      <button class="btn btn-default" v-on:click="hideModal"><i class="fa fa-fw fa-close"></i> {{ _('Close') }}</button>
      <button class="btn btn-success" v-on:click="sendPaymentLink" :disabled="!canSend"><i class="fa fa-send fa-fw"></i> {{ _('Send email') }}</button>
    </div>
  </div>
</template>

<script>
import errors from '~/lib/errors'
import constants from '~/lib/constants'
import { DateTime } from 'luxon'
import axios from 'axios'
import { mapGetters } from 'vuex'

export default {
  mixins: [errors, constants],
  props: ['originalOrder', 'baseDomain', 'shopId', 'locale', 'versions', 'apiKey', 'settings'],
  data () {
    return {
      order: {},
      email: '',
      subject: '',
      message: '',
      defaultSubject: null,
      defaultMessage: null,
      setting: false,
      version: null,
      texts: {
        'fi_FI': {
          'Payment link': 'Maksulinkki',
          'Order': 'Tilaus',
          'There is an an open order for you pending payment.': 'Avoin tilaus odottaa maksamista.'
        },
        'en_US': {
          'Payment link': 'Payment link',
          'Order': 'Order',
          'There is an an open order for you pending payment.': 'There is an an open order for you pending payment.'
        },
        'sv_SE': {
          'Payment link': 'Betalningslänk',
          'Order': 'Beställning',
          'There is an an open order for you pending payment.': 'Det finns en öppen order för dig i väntan på betalning.'
        },
        'de_DE': {
          'Payment link': 'Verknüpfungen der Zahlungsarten',
          'Order': 'Bestellung',
          'There is an an open order for you pending payment.': 'Es gibt eine offene Bestellung für Sie, die noch nicht bezahlt wurde.'
        },
        'it_IT': {
          'Payment link': 'Link per il pagamento',
          'Order': 'Ordine',
          'There is an an open order for you pending payment.': "C'è un ordine aperto per te in attesa di pagamento."
        },
        'es_ES': {
          'Payment link': 'Enlace de pago',
          'Order': 'Pida',
          'There is an an open order for you pending payment.': 'Hay un pedido abierto para usted pendiente de pago.'
        },
        'fr_FR': {
          'Payment link': 'Lien de paiement',
          'Order': 'Commande',
          'There is an an open order for you pending payment.': 'Une commande est en cours pour vous, en attente de paiement.'
        },
      }
    }
  },
  computed: {
    ...mapGetters(['dateFormat']),
    shortLocale () {
      return this.locale.split('_')[0]
    },
    canSend () {
      return this.paymentLink && this.paymentLinkEmail && this.order && this.order.identifier
    },
    expirationDate: {
      get () {
        if (!this.order.expirationdate) {
          return null
        }
        return DateTime.fromSeconds(Number(this.order.expirationdate), { zone: this.timezone }).toJSDate()
      },
      set (newValue) {
        this.order.expirationdate = DateTime.fromJSDate(newValue).toUnixInteger()
      }
    },
    setExpirationButtonIcon () {
      if (this.setting === 'processing') {
        return 'hourglass-o'
      } else if (this.setting === 'saved') {
        return 'check'
      }
      return 'save'
    },
    paymentLink () {
      if (!this.order || !this.order.identifier) {
        return ''
      }
      return this.baseDomain + this.shopId + '/' + this.paymentLinkVersion + '/pay/' + this.order.identifier.substring(0, 32)
    },
    paymentLinkVersion: {
      get () {
        if (!this.version) {
          return this.locale
        }
        return this.version
      },
      set (newValue) {
        this.version = newValue
      }
    },
    paymentLinkEmail: {
      get () {
        if (this.email) {
          return this.email
        }
        return this.order.email
      },
      set (newValue) {
        this.email = newValue
      }
    },
    paymentLinkSubject: {
      get () {
        if (this.subject) {
          return this.subject
        }
        if (this.defaultSubject) {
          return this.defaultSubject
        }
        return this.customTranslation('Payment link') + ': ' + this.companyName + ', ' + this.customTranslation('Order') + ': #' + this.order.id
      },
      set (newValue) {
        this.subject = newValue
      }
    },
    paymentLinkMessage: {
      get () {
        if (this.message) {
          return this.message
        }
        if (this.defaultMessage) {
          return this.defaultMessage
        }
        return this.customTranslation('There is an an open order for you pending payment.')
      },
      set (newValue) {
        this.message = newValue
      }
    },
    companyName () {
      return this.settings && this.settings.marketingname ? this.settings.marketingname : this.settings.companyname
    }
  },
  methods: {
    hideModal () {
      this.$emit('close')
    },
    customTranslation(text) {
      if (!this.texts[this.paymentLinkVersion] || !this.texts[this.paymentLinkVersion][text]) {
        return this._(text)
      }
      return this.texts[this.paymentLinkVersion][text]
    },
    async sendPaymentLink() {
      try {
        let response
        const data = {
          identifier: this.order.identifier.substring(0, 32),
          email: this.paymentLinkEmail,
          subject: this.paymentLinkSubject,
          message: this.paymentLinkMessage,
          version: this.paymentLinkVersion,
          showConfirm: true
        }
        response = await axios.post(this.baseDomain + this.shopId + '/' + this.paymentLinkVersion + '/pay/' + data.identifier + '.json?action=send&version=' + this.paymentLinkVersion + '&apikey=' + this.apiKey, data)
        this.showNotification(this._('Payment link sent!'))
        this.$emit('paymentLinkSent')
      } catch(e) {
        this.showError(e)
        return
      }
      this.hideModal()
    },
    getVersionName (version) {
      if (this.allVersions) {
        const v = this.allVersions.find(v => v.identifier === version)
        if (v) {
          return v.name
        }
      }
      return version
    },
    async setExpirationDate () {
      let response
      this.setting = 'processing'
      try {
        const data = {
          id: this.order.id,
          expirationdate: DateTime.fromJSDate(this.expirationDate).toFormat('d.M.yyyy'),
          source: 'editor'
        }
        response = await axios.patch(this.baseDomain + this.shopId + '/orders/' + this.order.id + '.json?apikey=' + this.apiKey, data)
      } catch(e) {
        this.setting = 'error'
        return
      }
      this.$emit('expirationDateSet')
      this.setting = 'saved'
      setTimeout(() => {
        this.setting = false
      }, 1000)
    },
    async loadMessage () {
      let response
      try {
        response = await axios.get(this.baseDomain + this.shopId + '/messages/paymentLink.json?apikey' + this.apiKey + '&version=' + this.version)
      } catch(e) {
        return
      }
      if (response && response.data) {
        if (response.data.text) {
          this.defaultMessage = response.data.text
        } else {
          this.defaultMessage = null
        }
        if (response.data.subject) {
          this.defaultSubject = response.data.subject
        } else {
          this.defaultSubject = null
        }
      }
    }
  },
  watch: {
    version () {
      if (!this.message) {
        this.loadMessage()
      }
    }
  },
  created() {
    if (this.originalOrder) {
      this.order = Object.assign({}, this.originalOrder)
      if (!this.order.version || this.order.version === 'fi_FI') {
        this.paymentLinkVersion = 'fi_FI'
      } else if (this.order.version === 'ru_RU') {
        this.paymentLinkVersion = 'en_US'
      } else {
        this.paymentLinkVersion = this.order.version
      }
    }
    this.loadMessage()
  }
}
</script>